// useRangeBoard — one week (or any window) of the dispatch board as route view
// models, the same shape the day board renders: routes per service day with
// technicianName / stops (computedStart, serviceCode, value, status …). Week
// and Jobs read from here so a job is the same job on every schedule screen.
import { useCallback, useEffect, useMemo, useState } from "react";
import { getDispatchBoard } from "../../../api/schedulingApi";
import { getIndustryKey, getSchedulingConfig } from "../../../constants/uiRegistry";
import { buildDispatchState, buildRouteViewModels, clean } from "../dispatch/utils";
import { computeRouteSchedule } from "../dispatch/utils/schedule";

const CONSTRAINTS = { maxAppointmentsPerTech: 12, maxDriveMinutes: 300, valueTarget: 1200, maxDueDateDeviation: "", balanceRoutes: true, priorities: { drive: 0.4, value: 0.4, cadence: 0.2 } };

export const isoDate = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
};
export const todayIso = () => isoDate(new Date());
export const shiftIso = (iso, days) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + days); return isoDate(d); };
/** Monday of the week holding `iso`. */
export const weekStartOf = (iso) => { const d = new Date(`${iso}T12:00:00`); const diff = (d.getDay() + 6) % 7; d.setDate(d.getDate() - diff); return isoDate(d); };
export const weekDays = (start) => Array.from({ length: 7 }, (_, i) => shiftIso(start, i));
export const dayLabel = (iso, opts = { weekday: "short", month: "numeric", day: "numeric" }) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, opts);

export default function useRangeBoard(startDate, endDate) {
  const [state, setState] = useState({ loading: true, error: "", routes: [], board: null });
  const industryKey = getIndustryKey("general");
  const schedulingConfig = useMemo(() => getSchedulingConfig(industryKey), [industryKey]);

  // One request per day through the DAY board — the path dispatch uses — so a
  // stop carries the same service name/code, times and value here as on the
  // board. (The range view returns FieldRoutes service-type ids instead of names.)
  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    const days = [];
    for (let d = startDate; d <= endDate; d = shiftIso(d, 1)) days.push(d);
    try {
      const boards = await Promise.all(days.map((day) =>
        getDispatchBoard({ day, view: "day", start_date: day, end_date: day, include_opportunities: false }).then((res) => ({ day, board: res?.data || {} }))));
      const routes = boards.flatMap(({ day, board }) => {
        if (!Array.isArray(board.routes)) return [];
        const st = buildDispatchState({ board, day, schedules: [], serviceTypes: [], schedulingConfig });
        return buildRouteViewModels(st, CONSTRAINTS).map((r) => ({
          ...r,
          serviceDay: clean(r.serviceDay) || day,
          stops: computeRouteSchedule(r.stops || []),
        }));
      });
      setState({ loading: false, error: "", routes, board: null });
    } catch (e) {
      setState({ loading: false, error: clean(e?.response?.data?.detail || e?.message) || "Unable to load the schedule.", routes: [], board: null });
    }
  }, [startDate, endDate, schedulingConfig]);

  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
}

/** Every stop in the window, flattened with its route's day and tech. */
export const flattenStops = (routes) =>
  routes.flatMap((r) => (r.stops || []).map((s) => ({
    ...s,
    serviceDay: r.serviceDay,
    technicianName: clean(r.technicianId) ? r.technicianName : "",
    routeId: r.id,
  })));

/** "Oct 19 – 25, 2026" · "Oct 26 – Nov 1, 2026" · "Dec 28, 2026 – Jan 3, 2027". */
export const weekRangeLabel = (a, b) => {
  const A = new Date(`${a}T12:00:00`); const B = new Date(`${b}T12:00:00`);
  const mon = (d) => d.toLocaleDateString(undefined, { month: "short" });
  if (A.getFullYear() !== B.getFullYear()) return `${mon(A)} ${A.getDate()}, ${A.getFullYear()} – ${mon(B)} ${B.getDate()}, ${B.getFullYear()}`;
  if (A.getMonth() !== B.getMonth()) return `${mon(A)} ${A.getDate()} – ${mon(B)} ${B.getDate()}, ${B.getFullYear()}`;
  return `${mon(A)} ${A.getDate()} – ${B.getDate()}, ${B.getFullYear()}`;
};
